import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../../contexts/authContext';
import { onNodesChange, addNode as addFileNode, deleteNode as deleteFileNode, updateNodeDetails, updateNodesOrder } from '../../firebase/files';
import { onMaterialsChange, addMaterial, updateMaterial } from '../../firebase/materials';
import { onFurnitureChange, addFurniture, updateFurniture } from '../../firebase/furniture';
import { onDecorativeImagesChange, addDecorativeImage, updateDecorativeImage } from '../../firebase/decorativeImages';
import { db, storage } from '../../firebase/firebase';
import { getDocs, collection, doc, writeBatch, deleteDoc } from 'firebase/firestore';
import { ref, deleteObject } from "firebase/storage";
import { updateProjectDetails } from '../../firebase/projects';
import { onUsersChange } from '../../firebase/users';
import { onUnreadNotificationsChange } from '../../firebase/notifications';
import { Upload, ChevronLeft, ChevronRight, Undo, Redo, Save, MousePointer2, Spline, Circle, Square, CheckSquare } from 'lucide-react';
import * as pdfjsLib from 'pdfjs-dist';
import { v4 as uuidv4 } from 'uuid';

import { useMoodboardHistory } from './useMoodboardHistory';
import { CombinedLibrary } from './CombinedLibrary';
import { DeleteConfirmationModal, ImageInfoModal } from './Modals';
import { FloatingMoodboardBar } from './FloatingBar';
import { MessagingPanel } from './MessagingPanel';
import { TopToolbar } from './Toolbar';
import { LeftPanel, RightPanel } from './Panels';
import { CanvasRenderer } from './CanvasRenderer';
import { MobileView } from './MobileView';
import { MaterialEditor } from './MaterialComponents';
import { FurnitureEditor } from './FurnitureComponents';
import { DecorativeImageEditor } from './DecorativeImageComponents';
import { uploadTexture } from '../../firebase/materials';
import ContextMenu from './ContextMenu';
import { bringToFront, sendToBack } from './layerUtils';

// Use a CDN to load the worker to avoid MIME type issues on some hosting providers.
// The version should match the one in package.json.
pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdn.jsdelivr.net/npm/pdfjs-dist@4.0.379/build/pdf.worker.min.mjs`;

const Moodboard = ({ projectId, projectOwnerId, project }) => {
    const { currentUser } = useAuth();
    const [users, setUsers] = useState([]);
    const [isPreview, setIsPreview] = useState(false);
    const canManageProject = currentUser?.uid === projectOwnerId && currentUser.role !== 'customer';
    const canManageProperties = (currentUser.role === 'superadmin' || currentUser.role === 'admin' || currentUser.role === 'collaborator');
    
    const effectiveIsPreview = isPreview || currentUser.role === 'customer';
    
    const canAutosave = (canManageProject && (project?.settings?.autoSaveMoodboard ?? true)) || (currentUser.role === 'customer');

    const { 
        currentPlanStates, 
        updateHistory, 
        undo, 
        redo, 
        canUndo, 
        canRedo, 
        saveStatus, 
        isSaving,
        handleSave, 
        hasUnsavedChanges,
        isHistoryInitialized
    } = useMoodboardHistory(project, projectId, canAutosave);

    const canEdit = (canManageProject || canManageProperties) && !effectiveIsPreview;
    
    useEffect(() => {
        if (!currentUser) return;
        const unsubscribe = onUsersChange(currentUser, (newUsers) => {
            let finalUsers = newUsers;
            if (!newUsers.some(u => u.id === currentUser.uid)) {
                finalUsers = [currentUser, ...newUsers];
            }
            setUsers(finalUsers);
        }, (error) => {
            console.error(`Error fetching users: ${error.message}`);
        });
        return () => unsubscribe();
    }, [currentUser]);

    useEffect(() => {
        if (!currentUser?.uid) return;
        const unsub = onUnreadNotificationsChange(currentUser.uid, setUnreadCounts);
        return () => unsub();
    }, [currentUser?.uid]);
    
    useEffect(() => {
        if (project) {
            const participants = [project.userId, ...(project.authorizedUsers || [])];
            setProjectParticipants([...new Set(participants)]);
        }
    }, [project]);

    const materialOwnerId = useMemo(() => {
        if (project?.materialOwnerId) {
            return project.materialOwnerId;
        }
        if (!project || !users.length) return null;
        const owner = users.find(u => u.id === project.userId);
        if (!owner) return null;
        if (owner.role === 'admin' || owner.role === 'superadmin') {
            return owner.id;
        }
        return owner.createdBy || null;
    }, [project, users]);

    const [isLeftPanelOpen, setIsLeftPanelOpen] = useState(true);
    const [isRightPanelOpen, setIsRightPanelOpen] = useState(true);

    const [mobileView, setMobileView] = useState('canvas');
    const [isMobileOpacityPanelOpen, setIsMobileOpacityPanelOpen] = useState(false);

    const [floorPlans, setFloorPlans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editingPlanId, setEditingPlanId] = useState(null);
    const [tempName, setTempName] = useState('');
    const [activePlanId, setActivePlanId] = useState(null);
    const [expandedPlanId, setExpandedPlanId] = useState(null);
    
    const [draggedPlanId, setDraggedPlanId] = useState(null);
    const [dropIndicator, setDropIndicator] = useState(null);
    const [draggedOverPlanId, setDraggedOverPlanId] = useState(null);

    const fileInputRef = useRef(null);
    const [uploadTargetPlanId, setUploadTargetPlanId] = useState(null);
    const [uploadProgress, setUploadProgress] = useState({});

    const [isDeleting, setIsDeleting] = useState(false);
    const [nodeToDelete, setNodeToDelete] = useState(null);

    const desktopCanvasRef = useRef(null);
    const mobileCanvasRef = useRef(null);
    const [transform, setTransform] = useState({ x: 0, y: 0, k: 1 });
    const [isPanning, setIsPanning] = useState(false);
    const [lastMouse, setLastMouse] = useState({ x: 0, y: 0 });
    const [pdfPage, setPdfPage] = useState(null);
    const [isRendering, setIsRendering] = useState(false);
    const [isDark, setIsDark] = useState(false);

    const transformRef = useRef(transform);
    useEffect(() => { transformRef.current = transform; }, [transform]);

    const [planOpacity, setPlanOpacity] = useState(1);
    const [entitiesOpacity, setEntitiesOpacity] = useState(1);

    const [tool, setTool] = useState('select');
    
    const [showConfirmations, setShowConfirmations] = useState(true);
    const [contextMenu, setContextMenu] = useState({ show: false, x: 0, y: 0, entityId: null });

    const [editingItem, setEditingItem] = useState({ type: null, item: null });

    const rawEntities = currentPlanStates?.[activePlanId] || [];
    
    const entities = useMemo(() => (
        rawEntities.map((entity, index) => {
            let newEntity = { ...entity };
    
            if (newEntity.zIndex === undefined) {
                if (newEntity.createdAt && newEntity.createdAt.toMillis) {
                    newEntity.zIndex = newEntity.createdAt.toMillis();
                } else {
                    newEntity.zIndex = index;
                }
            }

            if (entity.type === 'image') {
                newEntity.rotation = entity.rotation || 0;
                return newEntity;
            };

            const applyEdgeDefaults = (edge = {}) => {
                const defaults = { name: '', wallMaterials: [], textSize: 12, textColor: '#047857' };
                if (!('textSize' in edge)) {
                    return { ...defaults, ...edge };
                }
                 if (!Array.isArray(edge.wallMaterials)) {
                    return { ...edge, wallMaterials: [] };
                }
                return edge;
            };

            const createDefaultEdges = (count) => Array(count).fill(null).map(() => applyEdgeDefaults({}));

            if (newEntity.type === 'polygon') {
                const numEdges = newEntity.points?.length || 0;
                if (!newEntity.edges || newEntity.edges.length !== numEdges) {
                    newEntity.edges = createDefaultEdges(numEdges);
                } else {
                    newEntity.edges = newEntity.edges.map(applyEdgeDefaults);
                }
                return newEntity;
            }
            if (newEntity.type === 'rect') {
                if (!newEntity.edges || newEntity.edges.length !== 4) {
                    newEntity.edges = createDefaultEdges(4);
                } else {
                    newEntity.edges = newEntity.edges.map(applyEdgeDefaults);
                }
                return newEntity;
            }
            if (newEntity.type === 'ellipse') {
                if (!newEntity.edges || newEntity.edges.length !== 1) {
                    newEntity.edges = createDefaultEdges(1);
                } else {
                    newEntity.edges = newEntity.edges.map(applyEdgeDefaults);
                }
                return newEntity;
            }
            return newEntity;
        })
    ), [rawEntities]);

    const [drawingPoints, setDrawingPoints] = useState([]);
    const [dragStart, setDragStart] = useState(null);
    const [currentMouseData, setCurrentMouseData] = useState({ pos: null, shiftKey: false, altKey: false });
    const [selectedEntityIds, setSelectedEntityIds] = useState([]);
    const [focusedAreaId, setFocusedAreaId] = useState(null);
    const [selectedEdgeIndices, setSelectedEdgeIndices] = useState([]);
    const [draggedImageState, setDraggedImageState] = useState(null);
    const [draggedAnchorState, setDraggedAnchorState] = useState(null);

    const [materials, setMaterials] = useState([]);
    const [furniture, setFurniture] = useState([]);
    const [decorativeImages, setDecorativeImages] = useState([]);
    const [isLibraryOpen, setIsLibraryOpen] = useState(false);
    const [libraryPurpose, setLibraryPurpose] = useState('add'); // 'add' or 'assign'
    const [materialTarget, setMaterialTarget] = useState({ key: null, index: -1, type: null });
    const [libraryInitialTab, setLibraryInitialTab] = useState('materials');

    const [displayMode, setDisplayMode] = useState('material');
    
    const [imageInfoModalData, setImageInfoModalData] = useState(null);
    const [isDraggingOverCanvas, setIsDraggingOverCanvas] = useState(false);
    const dragCounter = useRef(0);

    const [unreadCounts, setUnreadCounts] = useState({});
    const [feedbackElement, setFeedbackElement] = useState(null);
    const [projectParticipants, setProjectParticipants] = useState([]);

    const pointersRef = useRef(new Map());
    const lastCenterRef = useRef(null);
    const lastDistRef = useRef(0);
    
    const getDistance = (p1, p2) => Math.hypot(p2.clientX - p1.clientX, p2.clientY - p1.clientY);
    const getMidpoint = (p1, p2) => ({ clientX: (p1.clientX + p2.clientX) / 2, clientY: (p1.clientY + p2.clientY) / 2 });
    
    useEffect(() => {
        setIsDark(document.documentElement.classList.contains('dark'));
        const observer = new MutationObserver(() => {
            setIsDark(document.documentElement.classList.contains('dark'));
        });
        observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        const handleClickOutside = () => {
            if (contextMenu.show) {
                setContextMenu({ show: false, x: 0, y: 0, entityId: null });
            }
        };
        window.addEventListener('click', handleClickOutside);
        return () => window.removeEventListener('click', handleClickOutside);
    }, [contextMenu.show]);

    const activePlan = floorPlans.find(plan => plan.id === activePlanId);
    const selectedEntity = useMemo(() => selectedEntityIds.length === 1 ? entities.find(e => e.id === selectedEntityIds[0]) : null, [entities, selectedEntityIds]);
    const singleSelectedEntityId = useMemo(() => selectedEntityIds.length === 1 ? selectedEntityIds[0] : null, [selectedEntityIds]);
    
    const activeAreaId = useMemo(() => {
        if (!selectedEntity) return null;
        if (['polygon', 'rect', 'ellipse'].includes(selectedEntity.type)) {
            return selectedEntity.id;
        }
        if (selectedEntity.type === 'image' && selectedEntity.linkedAreaId) {
            return selectedEntity.linkedAreaId;
        }
        return null;
    }, [selectedEntity]);

    const focusedArea = useMemo(() => entities.find(e => e.id === focusedAreaId), [entities, focusedAreaId]);

    const renderableEntities = useMemo(() => {
        if (effectiveIsPreview) {
            return entities; // In preview, render all entities and let the renderer handle visibility
        }
        
        // Logic for non-preview (edit) mode
        const areaInFocus = activeAreaId;

        if (areaInFocus) {
            const allLinkedAndArea = entities.filter(e => e.id === areaInFocus || e.linkedAreaId === areaInFocus);
            
            return allLinkedAndArea.filter(e => {
                if (['polygon', 'rect', 'ellipse'].includes(e.type)) {
                    return true;
                }

                if (e.type === 'image') {
                    if (e.purpose === 'material' && displayMode === 'material') return true;
                    if (e.purpose === 'furniture' && displayMode === 'furniture') return true;
                    if (e.purpose === 'decorative') return true;
                }
                
                return false;
            });
        }
        
        return entities.filter(e => {
            if (['polygon', 'rect', 'ellipse'].includes(e.type)) return true;
            if (e.type === 'image' && !e.linkedAreaId && e.purpose === 'decorative') return true;
            return false;
        });
    }, [entities, effectiveIsPreview, activeAreaId, displayMode]);
    
    useEffect(() => {
        if (project && !project.materialOwnerId && canManageProject && materialOwnerId) {
            updateProjectDetails(projectId, { materialOwnerId: materialOwnerId });
        }
    }, [project, canManageProject, materialOwnerId, projectId]);

    useEffect(() => {
        if (!materialOwnerId) {
            setMaterials([]);
            return;
        }
        const unsub = onMaterialsChange(materialOwnerId, setMaterials);
        return () => unsub();
    }, [materialOwnerId]);

    useEffect(() => {
        if (!materialOwnerId) {
            setFurniture([]);
            return;
        }
        const unsub = onFurnitureChange(materialOwnerId, setFurniture);
        return () => unsub();
    }, [materialOwnerId]);

    useEffect(() => {
        if (!materialOwnerId) {
            setDecorativeImages([]);
            return;
        }
        const unsub = onDecorativeImagesChange(materialOwnerId, setDecorativeImages);
        return () => unsub();
    }, [materialOwnerId]);


    useEffect(() => {
        if (activePlan && activePlan.url) {
            const renderPdf = async () => {
                setIsRendering(true);
                setPdfPage(null);
                try {
                    const loadingTask = pdfjsLib.getDocument(activePlan.url);
                    const pdf = await loadingTask.promise;
                    const page = await pdf.getPage(1);
                    const viewport = page.getViewport({ scale: 2.0 }); 

                    const canvas = document.createElement('canvas');
                    const context = canvas.getContext('2d');
                    canvas.height = viewport.height;
                    canvas.width = viewport.width;

                    await page.render({ canvasContext: context, viewport }).promise;

                    setPdfPage({
                        src: canvas.toDataURL(),
                        width: viewport.width,
                        height: viewport.height,
                    });
                } catch (error) {
                    console.error("Error rendering PDF:", error);
                    setPdfPage(null);
                } finally {
                    setIsRendering(false);
                }
            };
            renderPdf();
        } else {
            setPdfPage(null);
        }
    }, [activePlan]);

    useEffect(() => {
        if (!projectId) return;
        setLoading(true);
        const unsubscribe = onNodesChange(projectId, "moodboard-floorplans", (newPlans) => {
            setFloorPlans(newPlans);
            setLoading(false);
        });
        return () => unsubscribe();
    }, [projectId]);

    useEffect(() => {
        if (!loading && floorPlans.length > 0 && !activePlanId) {
            const defaultPlanId = project.defaultPlanId;
            const planExists = floorPlans.some(p => p.id === defaultPlanId);
            const firstPlanId = floorPlans.length > 0 ? floorPlans[0].id : null;
            
            const initialPlanId = planExists ? defaultPlanId : firstPlanId;

            if (initialPlanId) {
                setActivePlanId(initialPlanId);
                
                const initialPlanEntities = currentPlanStates?.[initialPlanId] || [];
                if (initialPlanEntities?.length > 0) {
                    setExpandedPlanId(initialPlanId);
                }
            }
        }
    }, [loading, floorPlans, activePlanId, project.defaultPlanId, currentPlanStates]);

    const updateEntities = useCallback((newEntitiesForActivePlan) => {
        if (!activePlanId) return;

        const currentHistoryState = currentPlanStates || {};

        if (newEntitiesForActivePlan.length > (currentHistoryState[activePlanId] || []).length) {
            setExpandedPlanId(activePlanId);
        }

        const newHistoryState = {
            ...currentHistoryState,
            [activePlanId]: newEntitiesForActivePlan
        };
        
        updateHistory(newHistoryState);
    }, [activePlanId, currentPlanStates, updateHistory]);

    const getEntityCentroid = (entity) => {
        if (!entity) return { x: 0, y: 0 };
        switch (entity.type) {
            case 'polygon':
                if (!entity.points || entity.points.length === 0) return { x: 0, y: 0 };
                const total = entity.points.reduce((acc, p) => ({ x: acc.x + p.x, y: acc.y + p.y }), { x: 0, y: 0 });
                return { x: total.x / entity.points.length, y: total.y / entity.points.length };
            case 'rect':
                return { x: entity.x + entity.width / 2, y: entity.y + entity.height / 2 };
            case 'ellipse':
                return { x: entity.cx, y: entity.cy };
            default:
                return { x: 0, y: 0 };
        }
    };

    const manageMaterialBox = useCallback((updatedArea, currentEntities, materials, isDark) => {
        let nextEntities = [...currentEntities];
    
        const createOrUpdateBox = (category, materialIds, linkedAreaId, existingBox, options = {}) => {
            const { edgeIndex, floorSlotKey, wallSlotKey, wallSlotName, floorSlotName } = options;
            
            if (materialIds && materialIds.length > 0) {
                const activeIndex = existingBox?.activeMaterialIndex !== undefined && existingBox.activeMaterialIndex < materialIds.length ? existingBox.activeMaterialIndex : 0;
                const activeMaterialId = materialIds[activeIndex];
                const activeMaterial = materials.find(m => m.id === activeMaterialId);
    
                let boxName;
                if (category === 'floor') boxName = floorSlotName || 'Floor Material';
                else if (category === 'wall') boxName = wallSlotName || 'Wall Material';
                else if (category === 'customWall') {
                    const edge = updatedArea.edges[edgeIndex];
                    boxName = edge?.name ? `${edge.name} Material` : `Edge ${edgeIndex + 1} Material`;
                } else {
                    boxName = `${category.charAt(0).toUpperCase() + category.slice(1)} Material`;
                }
    
                const boxData = {
                    materialIds: materialIds,
                    activeMaterialIndex: activeIndex,
                    name: activeMaterial?.name || boxName,
                    imageUrl: activeMaterial?.textureUrl || null,
                    color: activeMaterial?.color || '#cccccc',
                    description: activeMaterial?.description || '',
                    price: activeMaterial?.price || 0,
                    currency: activeMaterial?.currency || 'USD',
                    linkUrl: activeMaterial?.linkUrl || '',
                    slotName: category === 'floor' ? floorSlotName : (category === 'wall' ? wallSlotName : null),
                };
    
                if (existingBox) {
                    const boxIndex = nextEntities.findIndex(e => e.id === existingBox.id);
                    if (boxIndex !== -1) {
                        nextEntities[boxIndex] = { ...nextEntities[boxIndex], ...boxData };
                    }
                } else {
                    const areaCentroid = getEntityCentroid(updatedArea);
                    const masterImageStyle = project.masterStyle?.image || {};
                    
                    const existingBoxesForArea = nextEntities.filter(
                        e => e.linkedAreaId === updatedArea.id && e.purpose === 'material'
                    ).length;
                    
                    const offsetX = (existingBoxesForArea % 3) * 170 - 170;
                    const offsetY = 150 + Math.floor(existingBoxesForArea / 3) * 170;

                    const newBox = {
                        id: `ent-${uuidv4()}`,
                        type: 'image',
                        purpose: 'material',
                        materialCategory: category,
                        x: areaCentroid.x + offsetX,
                        y: areaCentroid.y + offsetY,
                        width: 150,
                        height: 150,
                        rotation: 0,
                        opacity: 1,
                        zIndex: Date.now(),
                        borderRadius: masterImageStyle.borderRadius ?? 8,
                        borderWidth: masterImageStyle.borderWidth ?? 0,
                        borderColor: masterImageStyle.borderColor ?? '#000000',
                        colorOverlayEnabled: masterImageStyle.colorOverlayEnabled ?? false,
                        colorOverlay: masterImageStyle.colorOverlay ?? '#000000',
                        colorOverlayOpacity: masterImageStyle.colorOverlayOpacity ?? 0.4,
                        linkedAreaId: linkedAreaId,
                        linkAnchorX: areaCentroid.x,
                        linkAnchorY: areaCentroid.y,
                        linkColor: masterImageStyle.linkColor ?? (isDark ? '#FFFFFF' : '#000000'),
                        linkWidth: masterImageStyle.linkWidth ?? 2,
                        isLinkVisible: masterImageStyle.isLinkVisible ?? true,
                        ...boxData,
                    };
                    if (edgeIndex !== undefined) newBox.edgeIndex = edgeIndex;
                    if (floorSlotKey) newBox.floorSlotKey = floorSlotKey;
                    if (wallSlotKey) newBox.wallSlotKey = wallSlotKey;
                    nextEntities.push(newBox);
                }
            } else {
                if (existingBox) {
                    nextEntities = nextEntities.filter(e => e.id !== existingBox.id);
                }
            }
        };
        
        // Floor Slots
        if (updatedArea.floorSlots) {
            updatedArea.floorSlots.forEach(slot => {
                const existingBox = nextEntities.find(e => e.linkedAreaId === updatedArea.id && e.materialCategory === 'floor' && e.floorSlotKey === slot.key);
                createOrUpdateBox('floor', slot.materialIds, updatedArea.id, existingBox, { floorSlotKey: slot.key, floorSlotName: slot.name });
            });
            const existingFloorBoxes = nextEntities.filter(e => e.linkedAreaId === updatedArea.id && e.materialCategory === 'floor');
            const activeSlotKeys = updatedArea.floorSlots.map(s => s.key);
            existingFloorBoxes.forEach(box => {
                if (!activeSlotKeys.includes(box.floorSlotKey)) {
                    nextEntities = nextEntities.filter(e => e.id !== box.id);
                }
            });
        }
        
        // Wall Slots
        if (updatedArea.wallSlots) {
            updatedArea.wallSlots.forEach(slot => {
                const existingBox = nextEntities.find(e => e.linkedAreaId === updatedArea.id && e.materialCategory === 'wall' && e.wallSlotKey === slot.key);
                createOrUpdateBox('wall', slot.materialIds, updatedArea.id, existingBox, { wallSlotKey: slot.key, wallSlotName: slot.name });
            });
            const existingWallBoxes = nextEntities.filter(e => e.linkedAreaId === updatedArea.id && e.materialCategory === 'wall');
            const activeSlotKeys = updatedArea.wallSlots.map(s => s.key);
            existingWallBoxes.forEach(box => {
                if (!activeSlotKeys.includes(box.wallSlotKey)) {
                    nextEntities = nextEntities.filter(e => e.id !== box.id);
                }
            });
        }
    
        // Standard categories
        ['ceiling'].forEach(category => {
            const materialIds = updatedArea[category + 'Materials'] || [];
            const existingBox = nextEntities.find(e => e.linkedAreaId === updatedArea.id && e.purpose === 'material' && e.materialCategory === category);
            createOrUpdateBox(category, materialIds, updatedArea.id, existingBox);
        });
    
        // Custom Edges
        if (updatedArea.edges) {
            updatedArea.edges.forEach((edge, edgeIndex) => {
                const materialIds = edge.wallMaterials || [];
                const existingBox = nextEntities.find(e => e.linkedAreaId === updatedArea.id && e.materialCategory === 'customWall' && e.edgeIndex === edgeIndex);
                createOrUpdateBox('customWall', materialIds, updatedArea.id, existingBox, { edgeIndex });
            });
            // cleanup for deleted edges
            const existingEdgeBoxes = nextEntities.filter(e => e.linkedAreaId === updatedArea.id && e.materialCategory === 'customWall');
            const activeEdgeIndicesWithMats = updatedArea.edges.map((e,i) => (e.wallMaterials && e.wallMaterials.length > 0) ? i : -1).filter(i => i !== -1);
            existingEdgeBoxes.forEach(box => {
                if (!activeEdgeIndicesWithMats.includes(box.edgeIndex)) {
                    nextEntities = nextEntities.filter(e => e.id !== box.id);
                }
            })
        }
    
        return nextEntities;
    }, [materials, isDark, project.masterStyle]);

    const handleAddFloor = async () => {
        if (!canManageProject) return;
        const newPlan = {
            name: `New Plan ${floorPlans.length + 1}`,
            type: 'file',
            parentId: 'moodboard-floorplans',
            fileType: 'placeholder/floorplan',
            url: null,
            size: 0,
            order: floorPlans.length,
        };
        try {
            const docRef = await addFileNode(projectId, newPlan, currentUser.uid);
            setActivePlanId(docRef.id);
        } catch (error) {
            console.error("Error creating new floor plan:", error);
        }
    };
    
    const handlePlanDragStart = (e, planId) => {
        if (!canManageProject) return;
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('application/plan-id', planId);
        setDraggedPlanId(planId);
    };

    const handlePlanDragOver = (e, planId) => {
        e.preventDefault();
        if (!draggedPlanId || draggedPlanId === planId) {
            setDropIndicator(null);
            return;
        }
    
        const rect = e.currentTarget.getBoundingClientRect();
        const isBottomHalf = (e.clientY - rect.top) > (rect.height / 2);
    
        if (!dropIndicator || dropIndicator.targetId !== planId || dropIndicator.position !== (isBottomHalf ? 'after' : 'before')) {
            setDropIndicator({ targetId: planId, position: isBottomHalf ? 'after' : 'before' });
        }
    };
    
    const handlePlanDragEnd = () => {
        setDraggedPlanId(null);
        setDropIndicator(null);
        setDraggedOverPlanId(null);
    };

    const handlePlanDrop = async (e) => {
        e.preventDefault();
        e.stopPropagation();

        const droppedPlanId = e.dataTransfer.getData('application/plan-id');
        if (droppedPlanId && dropIndicator && canManageProject) {
            let newPlans = [...floorPlans];
            const draggedItemIndex = newPlans.findIndex(p => p.id === droppedPlanId);
            
            if (draggedItemIndex === -1) { 
                handlePlanDragEnd(); 
                return; 
            }

            const [draggedItem] = newPlans.splice(draggedItemIndex, 1);
            
            const dropTargetIndex = newPlans.findIndex(p => p.id === dropIndicator.targetId);
            
            if (dropTargetIndex === -1) {
                 newPlans.splice(draggedItemIndex, 0, draggedItem);
                 setFloorPlans(newPlans);
                 handlePlanDragEnd();
                 return;
            }

            const insertionIndex = dropIndicator.position === 'after' 
                ? dropTargetIndex + 1 
                : dropTargetIndex;

            newPlans.splice(insertionIndex, 0, draggedItem);
            setFloorPlans(newPlans); 

            const orderedIds = newPlans.map(p => p.id);
            try {
                await updateNodesOrder(projectId, orderedIds);
            } catch (err) {
                console.error("Failed to reorder plans:", err);
                onNodesChange(projectId, "moodboard-floorplans", setFloorPlans);
            }
        }
        
        handlePlanDragEnd();
    };

    const handleDragLeaveContainer = (e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) {
          setDropIndicator(null);
        }
    };


    const handleDoubleClick = (plan) => {
        if (!canManageProject) return;
        setEditingPlanId(plan.id);
        setTempName(plan.name);
    };

    const handleNameChange = async () => {
        if (!canManageProject || !tempName.trim()) { 
            setEditingPlanId(null);
            return;
        }
        try {
            await updateNodeDetails(projectId, editingPlanId, { name: tempName });
        } catch (error) {
            console.error("Error renaming plan:", error);
        } finally {
            setEditingPlanId(null);
        }
    };
    
    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            handleNameChange();
        } else if (e.key === 'Escape') {
            setEditingPlanId(null);
        }
    }
    
    const handleSetActive = (planId) => {
        if (activePlanId !== planId) {
            setActivePlanId(planId);
            const newPlanHasEntities = (currentPlanStates?.[planId] || []).length > 0;
            if (newPlanHasEntities) {
                setExpandedPlanId(planId);
            } else {
                setExpandedPlanId(null);
            }
        }
    };

    const handleToggleExpand = (planId) => {
        setExpandedPlanId(prev => (prev === planId ? null : planId));
    };
    
    const processFile = async (file, planId) => {
        if (!canManageProject || !file || file.type !== 'application/pdf') {
            console.error("Invalid file type. Please use a PDF.");
            return;
        }
        setUploadProgress(prev => ({...prev, [planId]: 0 }));
        
        try {
            const filePath = `projects/${projectId}/files/${Date.now()}-${file.name}`;
            const url = await uploadTexture(file, (progress) => {
                 setUploadProgress(prev => ({...prev, [planId]: progress }));
            }, filePath);

            await updateNodeDetails(projectId, planId, {
                name: file.name.replace('.pdf', ''),
                url: url,
                size: file.size,
                fileType: file.type,
            });

        } catch (error) {
            console.error("Upload failed", error);
        } finally {
            setTimeout(() => {
                setUploadProgress(prev => {
                    const next = {...prev};
                    delete next[planId];
                    return next;
                });
            }, 3000);
        }
    }

    const handleDragEnterCard = (e, planId) => {
        e.preventDefault();
        e.stopPropagation();
        if (canManageProject && e.dataTransfer.types.includes('Files')) {
            setDraggedOverPlanId(planId);
        }
    };

    const handleDragLeaveCard = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (draggedOverPlanId === e.currentTarget.dataset.planId && !e.currentTarget.contains(e.relatedTarget)) {
            setDraggedOverPlanId(null);
        }
    };

    const handleCardDrop = (e, planId) => {
        e.preventDefault();
        e.stopPropagation();
        
        const isReordering = e.dataTransfer.types.includes('application/plan-id');
        const isUploading = e.dataTransfer.types.includes('Files');

        if (isReordering && canManageProject) {
            handlePlanDrop(e);
        } else if (isUploading && canManageProject) {
            setDraggedOverPlanId(null);
            const file = e.dataTransfer.files[0];
            if (file && file.type === 'application/pdf') {
                processFile(file, planId);
            }
        } else {
            handlePlanDragEnd();
        }
    };

    const handleFileSelect = (e) => {
        const file = e.target.files[0];
        if (uploadTargetPlanId) {
            processFile(file, uploadTargetPlanId);
        }
        e.target.value = null;
        setUploadTargetPlanId(null);
    };

    const triggerFileUpload = (planId) => {
        if (!canManageProject) return;
        setUploadTargetPlanId(planId);
        fileInputRef.current?.click();
    };

    const handleDeleteRequest = (node) => {
        if (!canEdit && node.type === 'image') {
            return;
        }
        if (!canManageProject && node.type !== 'image') return;
        setNodeToDelete(node);
    };
    
    const handleConfirmDelete = async () => {
        if (!nodeToDelete) return;
    
        setIsDeleting(true);
        try {
            if (['image', 'polygon', 'rect', 'ellipse'].includes(nodeToDelete.type)) {
    
                if (nodeToDelete.type === 'image' && nodeToDelete.imageUrl && nodeToDelete.imageUrl.includes('firebasestorage.googleapis.com')) {
                    const isUrlInUseByOtherEntities = entities.some(
                        ent => ent.id !== nodeToDelete.id && ent.imageUrl === nodeToDelete.imageUrl
                    );
    
                    if (!isUrlInUseByOtherEntities) {
                        try {
                            const imageRef = ref(storage, nodeToDelete.imageUrl);
                            await deleteObject(imageRef);
                        } catch (storageError) {
                            if (storageError.code !== 'storage/object-not-found') {
                                console.warn("Could not delete image from storage:", storageError);
                            }
                        }
                    }
                }
    
                let updatedEntities = entities;

                if (nodeToDelete.type === 'image' && nodeToDelete.purpose === 'material' && nodeToDelete.linkedAreaId) {
                    const { linkedAreaId, materialCategory, edgeIndex, floorSlotKey, wallSlotKey } = nodeToDelete;
                    updatedEntities = updatedEntities.map(ent => {
                        if (ent.id === linkedAreaId) {
                            const updatedArea = { ...ent };
                            if (materialCategory === 'customWall' && edgeIndex !== undefined && updatedArea.edges?.[edgeIndex]) {
                                const newEdges = [...updatedArea.edges];
                                newEdges[edgeIndex] = { ...newEdges[edgeIndex], wallMaterials: [] };
                                updatedArea.edges = newEdges;
                            } else if (materialCategory === 'floor' && floorSlotKey && updatedArea.floorSlots) {
                                updatedArea.floorSlots = updatedArea.floorSlots.map(slot => 
                                    slot.key === floorSlotKey ? { ...slot, materialIds: [] } : slot
                                );
                            } else if (materialCategory === 'wall' && wallSlotKey && updatedArea.wallSlots) {
                                updatedArea.wallSlots = updatedArea.wallSlots.map(slot => 
                                    slot.key === wallSlotKey ? { ...slot, materialIds: [] } : slot
                                );
                            } else if (['ceiling'].includes(materialCategory)) {
                                updatedArea[`${materialCategory}Materials`] = [];
                            }
                            return updatedArea;
                        }
                        return ent;
                    });
                }
                
                updatedEntities = updatedEntities.filter(ent => ent.id !== nodeToDelete.id);

                if (['polygon', 'rect', 'ellipse'].includes(nodeToDelete.type)) {
                    updatedEntities = updatedEntities.filter(ent => ent.linkedAreaId !== nodeToDelete.id);
                }

                updateEntities(updatedEntities);
    
                if (selectedEntityIds.includes(nodeToDelete.id)) {
                    setSelectedEntityIds(ids => ids.filter(id => id !== nodeToDelete.id));
                    setSelectedEdgeIndices([]);
                }
    
            } else if (nodeToDelete.parentId === 'moodboard-floorplans') {
                if (!canManageProject) return;
                await deleteFileNode(projectId, nodeToDelete.id);
                const planStateDocRef = doc(db, 'projects', projectId, 'moodboard_plans', nodeToDelete.id);
                await deleteDoc(planStateDocRef);
            }
        } catch (error) {
            console.error("Failed to delete item:", error);
        } finally {
            setIsDeleting(false);
            setNodeToDelete(null);
        }
    };

     const getSceneCoords = (e) => {
        let canvas = e.currentTarget;
        if (!canvas || !(canvas instanceof HTMLElement)) {
             canvas = e.touches?.[0]?.target;
        }
        if (!canvas || !(canvas instanceof HTMLElement)) {
            const canvasToUse = desktopCanvasRef.current ?? mobileCanvasRef.current;
            if (!canvasToUse) return { x: 0, y: 0 };
            canvas = canvasToUse;
        }
        const rect = canvas.getBoundingClientRect();
        const clientX = e.clientX ?? e.touches?.[0]?.clientX;
        const clientY = e.clientY ?? e.touches?.[0]?.clientY;

        if (clientX === undefined || clientY === undefined) {
             return { x: 0, y: 0 };
        }

        return {
            x: (clientX - rect.left - transform.x) / transform.k,
            y: (clientY - rect.top - transform.y) / transform.k
        };
    };

    const finalizeEntity = (newEntityData) => {
        const masterAreaStyle = project.masterStyle?.area || {};
        const newEntity = {
            id: `ent-${uuidv4()}`,
            ...newEntityData,
            name: `Area ${entities.filter(e => !e.type.includes('image')).length + 1}`,
            zIndex: Date.now(),
            fillColor: masterAreaStyle.fillColor ?? '#3b82f6',
            fillOpacity: masterAreaStyle.fillOpacity ?? 0.05,
        };

        updateEntities([...entities, newEntity]);
        setDrawingPoints([]);
        setDragStart(null);
    };

    const handleAddItem = (item, type) => {
        const canvas = desktopCanvasRef.current ?? mobileCanvasRef.current;
        if (!canvas || !activePlanId) return;
    
        const rect = canvas.getBoundingClientRect();
        const centerX = (rect.width / 2 - transform.x) / transform.k;
        const centerY = (rect.height / 2 - transform.y) / transform.k;
        
        const masterImageStyle = project.masterStyle?.image || {};
    
        const newEntity = {
          id: `ent-${uuidv4()}`,
          type: 'image',
          name: item.name || 'New Item',
          x: centerX,
          y: centerY,
          width: 200,
          height: 200,
          rotation: 0,
          opacity: 1,
          zIndex: Date.now(),
          borderRadius: masterImageStyle.borderRadius ?? 8,
          borderWidth: masterImageStyle.borderWidth ?? 0,
          borderColor: masterImageStyle.borderColor ?? '#000000',
          colorOverlayEnabled: masterImageStyle.colorOverlayEnabled ?? false,
          colorOverlay: masterImageStyle.colorOverlay ?? '#000000',
          colorOverlayOpacity: masterImageStyle.colorOverlayOpacity ?? 0.4,
          isLinkVisible: masterImageStyle.isLinkVisible ?? true,
        };
        
        if (type === 'material') {
          newEntity.purpose = 'material';
          newEntity.materialIds = [item.id];
          newEntity.activeMaterialIndex = 0;
          newEntity.imageUrl = item.textureUrl || null;
        } else if (type === 'furniture') {
          newEntity.purpose = 'furniture';
          newEntity.furnitureIds = [item.id];
          newEntity.activeFurnitureIndex = 0;
          newEntity.imageUrl = item.imageUrl || null;
          // Link to selected area if there is one
          if (singleSelectedEntityId && ['polygon', 'rect', 'ellipse'].includes(selectedEntity.type)) {
              newEntity.linkedAreaId = singleSelectedEntityId;
              const area = entities.find(a => a.id === singleSelectedEntityId);
              if (area) {
                  const centroid = getEntityCentroid(area);
                  newEntity.linkAnchorX = centroid.x;
                  newEntity.linkAnchorY = centroid.y;
                  newEntity.linkColor = masterImageStyle.linkColor ?? (isDark ? '#FFFFFF' : '#000000');
                  newEntity.linkWidth = masterImageStyle.linkWidth ?? 2;
              }
          }
        } else if (type === 'decorative') {
            newEntity.purpose = 'decorative';
            newEntity.decorativeImageId = item.id;
            newEntity.imageUrl = item.imageUrl;
        }
    
        const imageUrlForSizing = newEntity.imageUrl;
    
        if (imageUrlForSizing) {
            const img = new Image();
            if (imageUrlForSizing.includes('firebasestorage.googleapis.com')) {
                img.crossOrigin = "Anonymous";
            }
            img.onload = () => {
                 const originalAspectRatio = img.naturalWidth > 0 && img.naturalHeight > 0 ? img.naturalWidth / img.naturalHeight : 1;
                 newEntity.originalAspectRatio = originalAspectRatio;
                 newEntity.height = 200 / originalAspectRatio;
                 updateEntities([...entities, newEntity]);
            };
            img.onerror = () => {
                newEntity.originalAspectRatio = 1;
                updateEntities([...entities, newEntity]);
            };
            img.src = imageUrlForSizing;
        } else {
            newEntity.originalAspectRatio = 1;
            updateEntities([...entities, newEntity]);
        }
    };

    const handleDeleteCustomEdge = () => {
        if (!singleSelectedEntityId || !selectedEdgeIndices.length || !canEdit) return;
    
        let updatedArea;
        const entitiesWithResetEdges = entities.map(ent => {
            if (ent.id === singleSelectedEntityId) {
                const newEdges = ent.edges.map((edge, index) => {
                    if (selectedEdgeIndices.includes(index)) {
                        // Reset the edge to its default state
                        return { name: '', wallMaterials: [], textSize: 12, textColor: '#047857' };
                    }
                    return edge;
                });
                updatedArea = { ...ent, edges: newEdges };
                return updatedArea;
            }
            return ent;
        });
    
        if (!updatedArea) return;
    
        // Now, manage the material boxes based on the updated area
        const finalEntities = manageMaterialBox(updatedArea, entitiesWithResetEdges, materials, isDark);
        
        updateEntities(finalEntities);
        setSelectedEdgeIndices([]); // Deselect edges
    };

    const handlePropertyChange = (property, value, target = 'entity') => {
        if (selectedEntityIds.length !== 1) return;
        const currentEntityId = selectedEntityIds[0];
    
        let updatedEntities = entities.map(ent => {
            if (ent.id !== currentEntityId) return ent;
    
            if (target === 'entity') {
                if (property === 'linkedAreaId') {
                    const newAreaId = value;
                    const updatedEntity = { ...ent, linkedAreaId: newAreaId };
                    if (newAreaId) {
                        const area = entities.find(a => a.id === newAreaId);
                        if (area) {
                            const centroid = getEntityCentroid(area);
                            updatedEntity.linkAnchorX = centroid.x;
                            updatedEntity.linkAnchorY = centroid.y;
                            if (!updatedEntity.linkColor) updatedEntity.linkColor = isDark ? '#FFFFFF' : '#000000';
                            if (!updatedEntity.linkWidth) updatedEntity.linkWidth = 2;
                        }
                    } else {
                        delete updatedEntity.linkAnchorX;
                        delete updatedEntity.linkAnchorY;
                        delete updatedEntity.linkColor;
                        delete updatedEntity.linkWidth;
                    }
                    return updatedEntity;
                }
                return { ...ent, [property]: value };
            }
    
            if (target === 'edge' && selectedEdgeIndices.length > 0) {
                const newEdges = ent.edges.map((edge, index) => {
                    if (selectedEdgeIndices.includes(index)) {
                        return { ...edge, [property]: value };
                    }
                    return edge;
                });
                return { ...ent, edges: newEdges };
            }
            return ent;
        });
    
        const areaNeedsBoxUpdate =
            (target === 'entity' && ['floorSlots', 'wallSlots', 'ceilingMaterials'].includes(property)) ||
            (target === 'edge' && property === 'wallMaterials');
    
        if (areaNeedsBoxUpdate) {
            const updatedArea = updatedEntities.find(e => e.id === currentEntityId);
            if (updatedArea) {
                updatedEntities = manageMaterialBox(updatedArea, updatedEntities, materials, isDark);
            }
        }
    
        updateEntities(updatedEntities);
    };

    const handleRestoreAspectRatio = () => {
        if (!selectedEntity || selectedEntity.type !== 'image' || !canEdit || !selectedEntity.originalAspectRatio) return;

        const originalAspectRatio = selectedEntity.originalAspectRatio;
        if (originalAspectRatio > 0 && selectedEntity.width > 0) {
            const newHeight = selectedEntity.width / originalAspectRatio;
            handlePropertyChange('height', newHeight, 'entity');
        }
    };

    const handleDeleteSelectedEntity = async () => {
        if (!singleSelectedEntityId) return;
        setNodeToDelete(selectedEntity);
    };

    const handleEntityClick = (e, entityId) => {
        if (e.button === 1 && tool === 'select') {
            return;
        }

        if (tool === 'select') {
            e.stopPropagation();

            if (e.shiftKey && canEdit) {
                setSelectedEntityIds(prevIds => {
                    const newIds = prevIds.includes(entityId)
                        ? prevIds.filter(id => id !== entityId)
                        : [...prevIds, entityId];
                    
                    if (newIds.length > 1) {
                        setSelectedEdgeIndices([]);
                    }
                    return newIds;
                });
            } else {
                const entityIsAlreadyTheOnlyOneSelected = selectedEntityIds.length === 1 && selectedEntityIds[0] === entityId;
                if (!entityIsAlreadyTheOnlyOneSelected) {
                    setSelectedEdgeIndices([]);
                }
                setSelectedEntityIds([entityId]);
            }
            
            const entity = entities.find(e => e.id === entityId);
            if (!entity) return;

            if (effectiveIsPreview && !canEdit && entity.type === 'image') {
                let imageData = { ...entity };
                if (entity.purpose === 'material' && entity.materialIds?.length > 0 && materials) {
                    const activeMaterial = materials.find(m => m.id === entity.materialIds[entity.activeMaterialIndex || 0]);
                    if (activeMaterial) {
                        imageData.name = activeMaterial.name;
                        imageData.description = activeMaterial.description;
                        imageData.price = activeMaterial.price;
                        imageData.currency = activeMaterial.currency;
                        imageData.linkUrl = activeMaterial.linkUrl;
                        imageData.imageUrl = activeMaterial.textureUrl;
                    }
                } else if (entity.purpose === 'furniture' && entity.furnitureIds?.length > 0 && furniture) {
                    const activeFurniture = furniture.find(f => f.id === entity.furnitureIds[entity.activeFurnitureIndex || 0]);
                    if (activeFurniture) {
                        imageData.name = activeFurniture.name;
                        imageData.description = activeFurniture.description;
                        imageData.price = activeFurniture.price;
                        imageData.currency = activeFurniture.currency;
                        imageData.linkUrl = activeFurniture.linkUrl;
                        imageData.imageUrl = activeFurniture.imageUrl;
                    }
                } else if (entity.purpose === 'decorative' && entity.decorativeImageId && decorativeImages) {
                    const decorativeImage = decorativeImages.find(i => i.id === entity.decorativeImageId);
                    if (decorativeImage) {
                        imageData.name = decorativeImage.name;
                        imageData.description = decorativeImage.description;
                        imageData.price = decorativeImage.price;
                        imageData.currency = decorativeImage.currency;
                        imageData.linkUrl = decorativeImage.linkUrl;
                        imageData.imageUrl = decorativeImage.imageUrl;
                    }
                }
                setImageInfoModalData(imageData);
                return;
            }

            if (effectiveIsPreview) {
                let newFocusedAreaId = null;
                if (['polygon', 'rect', 'ellipse'].includes(entity.type)) {
                    newFocusedAreaId = entity.id;
                } else if (entity.type === 'image' && entity.linkedAreaId) {
                    newFocusedAreaId = entity.linkedAreaId;
                }
    
                if (focusedAreaId !== newFocusedAreaId) {
                    setFocusedAreaId(newFocusedAreaId);
                }
            }
        }
    };

    const handleEdgeClick = (e, edgeIndex) => {
        e.stopPropagation();
        if (tool !== 'select' || effectiveIsPreview) return;
        if (e.button === 1) return;
        
        const isShiftPressed = e.shiftKey;
        setSelectedEdgeIndices(prev => {
            if (isShiftPressed) {
                const isAlreadySelected = prev.includes(edgeIndex);
                if (isAlreadySelected) {
                    return prev.filter(i => i !== edgeIndex);
                } else {
                    return [...prev, edgeIndex];
                }
            } else {
                if (prev.length === 1 && prev[0] === edgeIndex) {
                    return [];
                }
                return [edgeIndex];
            }
        });
    };

    const handleSelectEntityFromPanel = (entityId, planId) => {
        handleSetActive(planId);
        setSelectedEntityIds([entityId]);
    
        if (effectiveIsPreview) {
            const planEntities = (currentPlanStates || {})[planId] || [];
            const entity = planEntities.find(e => e.id === entityId);
    
            if (entity && ['polygon', 'rect', 'ellipse'].includes(entity.type)) {
                setFocusedAreaId(entity.id);
            } else {
                setFocusedAreaId(null);
            }
        }
    };

    const handleSelectEdgeFromPanel = (edgeIndex) => {
        setSelectedEdgeIndices([edgeIndex]);
    };

    const handleClearEdgeSelection = () => {
        setSelectedEdgeIndices([]);
    };

    const onImageOrHandleMouseDown = (e, entityId, action = 'move') => {
        if (e.button === 1) { // Middle mouse button
            return;
        }
        e.stopPropagation();
        if (tool !== 'select' || !canEdit) return;

        setIsPanning(false);

        const entityToDrag = entities.find(ent => ent.id === entityId);
        if (!entityToDrag) return;


        const startSceneCoords = getSceneCoords(e);
        setDraggedImageState({
            action: action,
            entity: entityToDrag,
            startEntity: { ...entityToDrag },
            startPos: startSceneCoords,
            startClient: { x: e.clientX, y: e.clientY },
        });
    };

    const onAnchorMouseDown = (e, entityId) => {
        if (e.button === 1) { // Middle mouse button
            return;
        }
        e.stopPropagation();
        if (tool !== 'select' || !canEdit) return;

        const entity = entities.find(ent => ent.id === entityId);
        if (!entity || !entity.linkedAreaId) return;

        setIsPanning(false);
        const startSceneCoords = getSceneCoords(e);
        setDraggedAnchorState({
            entity: entity,
            startEntity: { ...entity },
            startPos: startSceneCoords,
        });
    };

    const handleMouseDown = (e) => {
        if (e.button === 1 || (e.altKey && tool === 'select')) {
            e.preventDefault();
            setIsPanning(true);
            setLastMouse({ x: e.clientX, y: e.clientY });
            return;
        }

        if (e.button !== 0) return;
        
        if (tool === 'select' && e.target === e.currentTarget && !draggedImageState && !draggedAnchorState) {
            setSelectedEntityIds([]);
            setSelectedEdgeIndices([]);
            if (effectiveIsPreview) {
                setFocusedAreaId(null);
            }
        }

        let pos = getSceneCoords(e);
        
        if (tool === 'polygon' && drawingPoints.length > 0 && e.ctrlKey) {
            const lastPoint = drawingPoints[drawingPoints.length - 1];
            const dx = Math.abs(pos.x - lastPoint.x);
            const dy = Math.abs(pos.y - lastPoint.y);
            if (dx > dy) {
                pos.y = lastPoint.y;
            } else {
                pos.x = lastPoint.x;
            }
        }

        setCurrentMouseData({ pos, shiftKey: e.shiftKey, altKey: e.altKey });

        if (tool === 'polygon') {
            if (!canEdit) return;
            if (drawingPoints.length > 2) {
                const first = drawingPoints[0];
                const dist = Math.hypot(pos.x - first.x, pos.y - first.y);
                if (dist < (1 / transform.k) * 10) {
                    finalizeEntity({ type: 'polygon', points: drawingPoints });
                    return;
                }
            }
            setDrawingPoints([...drawingPoints, pos]);
        } else if (tool === 'rect' || tool === 'ellipse') {
            if (!canEdit) return;
            setDragStart(pos);
        }
    };
    
    const handleMouseMove = (e) => {
        if (isPanning) {
            const dx = e.clientX - lastMouse.x;
            const dy = e.clientY - lastMouse.y;
            setTransform(prev => ({ ...prev, x: prev.x + dx, y: prev.y + dy }));
            setLastMouse({ x: e.clientX, y: e.clientY });
            return;
        }

        const pos = getSceneCoords(e);
        
        if (draggedImageState) {
            const { action, startEntity, startPos } = draggedImageState;
            
            const sceneDx = pos.x - startPos.x;
            const sceneDy = pos.y - startPos.y;
            
            let updatedEntity = { ...draggedImageState.entity };

            if (action === 'move') {
                updatedEntity.x = startEntity.x + sceneDx;
                updatedEntity.y = startEntity.y + sceneDy;
            } else if (action === 'rotate') {
                const center = { x: startEntity.x, y: startEntity.y };
                const startAngle = Math.atan2(startPos.y - center.y, startPos.x - center.x);
                const currentAngle = Math.atan2(pos.y - center.y, pos.x - center.x);
                let angleDiff = (currentAngle - startAngle) * (180 / Math.PI);
                let newRotation = (startEntity.rotation || 0) + angleDiff;

                if (e.shiftKey) {
                    newRotation = Math.round(newRotation / 15) * 15;
                }
                updatedEntity.rotation = newRotation;
            } else if (action.endsWith('-resize')) {
                const { sin, cos } = { 
                    sin: Math.sin(startEntity.rotation * Math.PI / 180), 
                    cos: Math.cos(startEntity.rotation * Math.PI / 180) 
                };
        
                const rotatedDx = sceneDx * cos + sceneDy * sin;
                const rotatedDy = -sceneDx * sin + sceneDy * cos;
                
                let dw = 0, dh = 0;
                if (action.includes('e')) dw = rotatedDx;
                if (action.includes('w')) dw = -rotatedDx;
                if (action.includes('s')) dh = rotatedDy;
                if (action.includes('n')) dh = -rotatedDy;
        
                const isCorner = action.length === 9;
                const aspectRatio = startEntity.originalAspectRatio || (startEntity.width > 0 ? startEntity.width / startEntity.height : 0);
        
                if (isCorner && e.shiftKey && aspectRatio > 0) {
                    const sign_dw = action.includes('w') ? -1 : 1;
                    const sign_dh = action.includes('n') ? -1 : 1;
        
                    if (Math.abs(dw) * startEntity.height > Math.abs(dh) * startEntity.width) {
                        dh = dw / aspectRatio * (sign_dw * sign_dh);
                    } else {
                        dw = dh * aspectRatio * (sign_dw * sign_dh);
                    }
                }
        
                let { width: newWidth, height: newHeight, x: newX, y: newY } = startEntity;
        
                if (e.altKey) {
                    newWidth = startEntity.width + dw * 2;
                    newHeight = startEntity.height + dh * 2;
                } else {
                    newWidth = startEntity.width + dw;
                    newHeight = startEntity.height + dh;
                    
                    const centerShiftX = dw / 2;
                    const centerShiftY = dh / 2;
                    
                    const worldShiftX = centerShiftX * cos - centerShiftY * sin;
                    const worldShiftY = centerShiftX * sin + centerShiftY * cos;
        
                    newX = startEntity.x + worldShiftX;
                    newY = startEntity.y + worldShiftY;
                }
                
                if (newWidth > 5 && newHeight > 5) {
                    updatedEntity.width = newWidth;
                    updatedEntity.height = newHeight;
                    updatedEntity.x = newX;
                    updatedEntity.y = newY;
                }
            }
            
            setDraggedImageState(prev => ({...prev, entity: updatedEntity}));

        } else if (draggedAnchorState) {
            const { startEntity, startPos } = draggedAnchorState;
            const areaEnt = entities.find(a => a.id === startEntity.linkedAreaId);
            if (!areaEnt) return;

            const originalAnchorX = startEntity.linkAnchorX ?? getEntityCentroid(areaEnt).x;
            const originalAnchorY = startEntity.linkAnchorY ?? getEntityCentroid(areaEnt).y;

            const sceneDx = pos.x - startPos.x;
            const sceneDy = pos.y - startPos.y;

            let updatedEntity = { ...draggedAnchorState.entity };
            updatedEntity.linkAnchorX = originalAnchorX + sceneDx;
            updatedEntity.linkAnchorY = originalAnchorY + sceneDy;

            setDraggedAnchorState(prev => ({ ...prev, entity: updatedEntity }));
            
        } else if (tool === 'polygon' && drawingPoints.length > 0 && e.ctrlKey) {
            const lastPoint = drawingPoints[drawingPoints.length - 1];
            const dx = Math.abs(pos.x - lastPoint.x);
            const dy = Math.abs(pos.y - lastPoint.y);
            if (dx > dy) {
                pos.y = lastPoint.y;
            } else {
                pos.x = lastPoint.x;
            }
        }
        setCurrentMouseData({ pos, shiftKey: e.shiftKey, altKey: e.altKey });
    };

    const handleMouseUp = (e) => {
        if (isPanning) {
            setIsPanning(false);
        }

        if (draggedImageState) {
            updateEntities(entities.map(ent => 
                ent.id === draggedImageState.entity.id ? draggedImageState.entity : ent
            ));
            setDraggedImageState(null);
        }

        if (draggedAnchorState) {
            updateEntities(entities.map(ent => 
                ent.id === draggedAnchorState.entity.id ? draggedAnchorState.entity : ent
            ));
            setDraggedAnchorState(null);
        }

        if (dragStart) {
            if (!canEdit) {
                setDragStart(null);
                return;
            }
            const endPos = getSceneCoords(e);
            let newEntity;
            const { shiftKey, altKey } = currentMouseData;

            if (tool === 'rect') {
                const x = Math.min(dragStart.x, endPos.x);
                const y = Math.min(dragStart.y, endPos.y);
                const width = Math.abs(dragStart.x - endPos.x);
                const height = Math.abs(dragStart.y - endPos.y);
                if (width > 2 && height > 2) {
                    newEntity = { type: 'rect', x, y, width, height };
                }
            } else if (tool === 'ellipse') {
                const finalX = endPos.x;
                const finalY = endPos.y;
                if(finalX !== undefined && finalY !== undefined) {
                    let cx, cy, rx, ry;
                    if (altKey) {
                        cx = dragStart.x;
                        cy = dragStart.y;
                        rx = Math.abs(finalX - dragStart.x);
                        ry = Math.abs(finalY - dragStart.y);
                    } else {
                        cx = dragStart.x + (finalX - dragStart.x) / 2;
                        cy = dragStart.y + (finalY - dragStart.y) / 2;
                        rx = Math.abs(finalX - dragStart.x) / 2;
                        ry = Math.abs(finalY - dragStart.y) / 2;
                    }

                    if (rx > 2 && ry > 2) {
                        if (shiftKey) {
                            const r = Math.max(rx, ry);
                            newEntity = { type: 'ellipse', cx, cy, rx: r, ry: r };
                        } else {
                            newEntity = { type: 'ellipse', cx, cy, rx, ry };
                        }
                    }
                }
            }
            
            if (newEntity) {
                finalizeEntity(newEntity);
            }
            setDragStart(null);
        }
    };

    const handleContextMenu = (e, entityId) => {
        e.preventDefault();
        e.stopPropagation();

        const entity = entities.find(ent => ent.id === entityId);
        if (!canEdit || !entity || entity.type !== 'image') {
            setContextMenu({ show: false, x: 0, y: 0, entityId: null });
            return;
        }
        
        setContextMenu({
            show: true,
            x: e.clientX,
            y: e.clientY,
            entityId: entityId,
        });
    };
    
    const handleBringToFront = () => {
        if (!contextMenu.entityId) return;
        const newEntities = bringToFront(entities, contextMenu.entityId);
        updateEntities(newEntities);
        setContextMenu({ show: false, x: 0, y: 0, entityId: null });
    };

    const handleSendToBack = () => {
        if (!contextMenu.entityId) return;
        const newEntities = sendToBack(entities, contextMenu.entityId);
        updateEntities(newEntities);
        setContextMenu({ show: false, x: 0, y: 0, entityId: null });
    };

    const handleRightClick = (e) => {
        e.preventDefault();
        if (drawingPoints.length > 0 || dragStart) {
            setDrawingPoints([]);
            setDragStart(null);
            setTool('select');
        }
    };
    
    const handleCanvasDragEnter = (e) => {
        e.preventDefault();
        e.stopPropagation();
        dragCounter.current++;
        if (canEdit && e.dataTransfer.items && e.dataTransfer.items.length > 0) {
            setIsDraggingOverCanvas(true);
        }
    };
    const handleCanvasDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        dragCounter.current--;
        if (dragCounter.current === 0) {
            setIsDraggingOverCanvas(false);
        }
    };
    const handleCanvasDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };
    const handleCanvasDrop = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDraggingOverCanvas(false);
        dragCounter.current = 0;
        if (!canEdit) return;

        const file = e.dataTransfer.files[0];
        if (file && file.type.startsWith('image/')) {
            try {
                const path = `projects/${projectId}/moodboard_images/${uuidv4()}-${file.name}`;
                const imageUrl = await uploadTexture(file, () => {}, path);
                
                const dropCoords = getSceneCoords(e);
                const img = new Image();
                img.onload = () => {
                     const originalAspectRatio = img.naturalWidth > 0 && img.naturalHeight > 0 ? img.naturalWidth / img.naturalHeight : 1;
                     const newImageEntity = {
                        id: `ent-${uuidv4()}`,
                        type: 'image',
                        name: file.name,
                        imageUrl,
                        x: dropCoords.x,
                        y: dropCoords.y,
                        width: 200,
                        height: 200 / originalAspectRatio,
                        rotation: 0,
                        originalAspectRatio,
                    };
                    updateEntities([...entities, newImageEntity]);
                };
                img.src = imageUrl;

            } catch (err) {
                console.error("Failed to upload dropped image:", err);
            }
        }
    };
    
    const handlePointerDown = (e) => {
        if (pointersRef.current.has(e.pointerId)) return;
        pointersRef.current.set(e.pointerId, { clientX: e.clientX, clientY: e.clientY });
        e.currentTarget.setPointerCapture(e.pointerId);
    
        if (pointersRef.current.size === 1) {
            setIsPanning(true);
            setLastMouse({ x: e.clientX, y: e.clientY });
        } else if (pointersRef.current.size === 2) {
            setIsPanning(false);
            const points = Array.from(pointersRef.current.values());
            lastDistRef.current = getDistance(points[0], points[1]);
            lastCenterRef.current = getMidpoint(points[0], points[1]);
        }
    };

    const handlePointerMove = (e) => {
        if (!pointersRef.current.has(e.pointerId)) return;
    
        pointersRef.current.set(e.pointerId, { clientX: e.clientX, clientY: e.clientY });
    
        if (pointersRef.current.size === 1) {
            if (!isPanning) return;
            const dx = e.clientX - lastMouse.x;
            const dy = e.clientY - lastMouse.y;
            setTransform(prev => ({ ...prev, x: prev.x + dx, y: prev.y + dy }));
            setLastMouse({ x: e.clientX, y: e.clientY });
        } else if (pointersRef.current.size === 2) {
            const lastCenter = lastCenterRef.current;
            if (!lastCenter) return;

            const points = Array.from(pointersRef.current.values());
            const newDist = getDistance(points[0], points[1]);
            const newCenter = getMidpoint(points[0], points[1]);
    
            setTransform(prevTransform => {
                const panDx = newCenter.clientX - lastCenter.clientX;
                const panDy = newCenter.clientY - lastCenter.clientY;
                const interimX = prevTransform.x + panDx;
                const interimY = prevTransform.y + panDy;
    
                const scaleMultiplier = lastDistRef.current > 0 ? newDist / lastDistRef.current : 1;
                const newK = Math.min(Math.max(0.1, 8), prevTransform.k * scaleMultiplier);
                
                const finalX = newCenter.clientX - (newCenter.clientX - interimX) * (newK / prevTransform.k);
                const finalY = newCenter.clientY - (newCenter.clientY - interimY) * (newK / prevTransform.k);
                
                return { x: finalX, y: finalY, k: newK };
            });
    
            lastDistRef.current = newDist;
            lastCenterRef.current = newCenter;
        }
    };
    

    const handlePointerUp = (e) => {
        pointersRef.current.delete(e.pointerId);
        try {
            e.currentTarget.releasePointerCapture(e.pointerId);
        } catch (error) {
        }

        if (pointersRef.current.size < 2) {
            lastCenterRef.current = null;
        }
        if (pointersRef.current.size < 1) {
            setIsPanning(false);
        }
    };
    
    const handleRecenter = useCallback(() => {
        let canvas = desktopCanvasRef.current ?? mobileCanvasRef.current;
        if (!pdfPage || !canvas) return;
        
        const container = canvas.getBoundingClientRect();
        if (container.width === 0 || container.height === 0) return;

        const padding = 50;
        const availableWidth = container.width - padding * 2;
        const availableHeight = container.height - padding * 2;

        const scaleX = availableWidth / pdfPage.width;
        const scaleY = availableHeight / pdfPage.height;
        const newK = Math.min(Math.min(scaleX, scaleY), 1);

        const newX = (container.width - pdfPage.width * newK) / 2;
        const newY = (container.height - pdfPage.height * newK) / 2;
        
        setTransform({ x: newX, y: newY, k: newK });
    }, [pdfPage]);

    const entitiesRef = useRef(entities);
    entitiesRef.current = entities;
    
    const handleRecenterRef = useRef(handleRecenter);
    handleRecenterRef.current = handleRecenter;

    const focusOnEntity = useCallback((entityId) => {
        const currentEntities = entitiesRef.current;
        const entity = currentEntities.find(e => e.id === entityId);
        const canvas = desktopCanvasRef.current ?? mobileCanvasRef.current;

        if (!entity || !canvas || !['polygon', 'rect', 'ellipse'].includes(entity.type)) {
            handleRecenterRef.current();
            return;
        }
    
        const getEntityBoundingBox = (ent) => {
            if (!ent) return null;
            if (ent.type === 'polygon' && ent.points?.length > 0) {
                let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
                ent.points.forEach(p => {
                    minX = Math.min(minX, p.x);
                    minY = Math.min(minY, p.y);
                    maxX = Math.max(maxX, p.x);
                    maxY = Math.max(maxY, p.y);
                });
                return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
            }
            if (ent.type === 'rect') return { x: ent.x, y: ent.y, width: ent.width, height: ent.height };
            if (ent.type === 'ellipse') return { x: ent.cx - ent.rx, y: ent.cy - ent.ry, width: ent.rx * 2, height: ent.ry * 2 };
            if (ent.type === 'image') return { x: ent.x - ent.width/2, y: ent.y - ent.height/2, width: ent.width, height: ent.height };
            return null;
        };
    
        let totalBBox = getEntityBoundingBox(entity);
        if (!totalBBox) { 
            handleRecenterRef.current(); 
            return; 
        }
    
        const linkedEntities = currentEntities.filter(e => e.linkedAreaId === entity.id && e.type === 'image');
        
        linkedEntities.forEach(linked => {
            const linkedBBox = getEntityBoundingBox(linked);
            if(linkedBBox) {
                const right = totalBBox.x + totalBBox.width;
                const bottom = totalBBox.y + totalBBox.height;
                const linkedRight = linkedBBox.x + linkedBBox.width;
                const linkedBottom = linkedBBox.y + linkedBBox.height;
    
                totalBBox.x = Math.min(totalBBox.x, linkedBBox.x);
                totalBBox.y = Math.min(totalBBox.y, linkedBBox.y);
                totalBBox.width = Math.max(right, linkedRight) - totalBBox.x;
                totalBBox.height = Math.max(bottom, linkedBottom) - totalBBox.y;
            }
        });
    
        const container = canvas.getBoundingClientRect();
        const padding = 80;
        const availableWidth = container.width - padding * 2;
        const availableHeight = container.height - padding * 2;
    
        const scaleX = totalBBox.width > 0 ? availableWidth / totalBBox.width : 1;
        const scaleY = totalBBox.height > 0 ? availableHeight / totalBBox.height : 1;
        const newK = Math.min(scaleX, scaleY);
    
        const newX = (container.width / 2) - (totalBBox.x + totalBBox.width / 2) * newK;
        const newY = (container.height / 2) - (totalBBox.y + totalBBox.height / 2) * newK;
    
        setTransform({ x: newX, y: newY, k: newK });
    
    }, []);

    useEffect(() => {
        if (focusedAreaId) {
            focusOnEntity(focusedAreaId);
        } else {
            handleRecenter();
        }
    }, [focusedAreaId, focusOnEntity, handleRecenter]);

    useEffect(() => {
        window.addEventListener('resize', handleRecenter);
        return () => {
            window.removeEventListener('resize', handleRecenter);
        };
    }, [handleRecenter]);

    useEffect(() => {
        const onWheel = (e) => {
            if (isPanning) return; // Don't zoom while panning
            if (e.button === 1) return;
            e.preventDefault();
            const { x, y, k } = transformRef.current;
            const scaleAmount = -e.deltaY * 0.001;
            const newK = Math.min(Math.max(0.1, 8), k * (1 + scaleAmount));

            const rect = e.currentTarget.getBoundingClientRect();
            const cursorX = e.clientX - rect.left;
            const cursorY = e.clientY - rect.top;

            const newX = cursorX - (cursorX - x) * (newK / k);
            const newY = cursorY - (cursorY - y) * (newK / k);

            setTransform({ x: newX, y: newY, k: newK });
        };

        const canvases = [desktopCanvasRef.current, mobileCanvasRef.current].filter(Boolean);
        canvases.forEach(canvas => canvas.addEventListener('wheel', onWheel, { passive: false }));

        return () => {
            canvases.forEach(canvas => canvas.removeEventListener('wheel', onWheel));
        };
    });

    const openLibrary = (purpose, options = {}) => {
        setLibraryPurpose(purpose);
        if (purpose === 'assign') {
          setMaterialTarget(options);
        }
        setLibraryInitialTab(options.initialTab || 'materials');
        setIsLibraryOpen(true);
    };

    const handleOpenEditor = (item, type) => {
        setEditingItem({ item, type });
    };

    const handleCloseEditor = () => {
        setEditingItem({ type: null, item: null });
    };

    const handleSaveItem = async (data) => {
        const { type, item } = editingItem;
        if (!type) return;

        const dataToSave = { ...data };
        if (!item) { // It's a new item
            dataToSave.ownerId = materialOwnerId;
        }

        try {
            if (type === 'material') {
                if (item) { await updateMaterial(item.id, dataToSave); } 
                else { await addMaterial(dataToSave); }
            } else if (type === 'furniture') {
                if (item) { await updateFurniture(item.id, dataToSave); }
                else { await addFurniture(dataToSave); }
            } else if (type === 'decorative') {
                if (item) { await updateDecorativeImage(item.id, dataToSave); }
                else { await addDecorativeImage(dataToSave); }
            }
            handleCloseEditor();
        } catch (error) {
            console.error(`Failed to save ${type}:`, error);
        }
    };


    const handleMaterialAssignment = (item) => {
        if (libraryPurpose !== 'assign' || !materialTarget.key || !selectedEntity) return;
    
        const { key, slotKey, materialIndex, index, type } = materialTarget;
    
        if (key === 'floorSlots' || key === 'wallSlots') {
            const slotsKey = key;
            const newSlots = selectedEntity[slotsKey].map(slot => {
                if (slot.key === slotKey) {
                    let newMaterialIds = [...(slot.materialIds || [])];
                    if (materialIndex === -1) {
                        newMaterialIds.push(item.id);
                    } else {
                        newMaterialIds[materialIndex] = item.id;
                    }
                    return { ...slot, materialIds: newMaterialIds };
                }
                return slot;
            });
            handlePropertyChange(slotsKey, newSlots, 'entity');
            setIsLibraryOpen(false);
            setMaterialTarget({ key: null, options: {} });
            return;
        }

        const updateTarget = type === 'edge' ? 'edge' : 'entity';
        let propertyKey = key;
        let currentMaterials;
    
        if (updateTarget === 'edge') {
            propertyKey = 'wallMaterials';
            const representativeEdge = selectedEntity.edges[selectedEdgeIndices[0]];
            currentMaterials = representativeEdge?.wallMaterials || [];
        } else {
            currentMaterials = selectedEntity[propertyKey] || [];
        }
    
        let newMaterials;
        if (index === -1) {
            newMaterials = [...currentMaterials, item.id];
        } else {
            newMaterials = [...currentMaterials];
            newMaterials[index] = item.id;
        }
    
        handlePropertyChange(propertyKey, newMaterials, updateTarget);
        setIsLibraryOpen(false);
        setMaterialTarget({ key: null, index: -1, type: null, slotKey: null });
    };

    const handleFurnitureAssignment = (item) => {
        if (libraryPurpose !== 'assign' || !materialTarget.key || !selectedEntity) return;
    
        const { key, index, type } = materialTarget;
    
        if (key !== 'furnitureIds' || type !== 'entity') return;
    
        const currentItems = selectedEntity.furnitureIds || [];
        let newItems;
    
        if (index === -1) { // Add new option
            newItems = [...currentItems, item.id];
        } else { // Change existing option (not used yet, but good to have)
            newItems = [...currentItems];
            newItems[index] = item.id;
        }
    
        handlePropertyChange('furnitureIds', newItems, 'entity');
        setIsLibraryOpen(false);
        setMaterialTarget({ key: null, index: -1, type: null, slotKey: null });
    };

    const handleItemSelectionFromLibrary = (item, type) => {
        if (libraryPurpose === 'assign') {
          if (type === 'material') {
            handleMaterialAssignment(item);
          } else if (type === 'furniture') {
            handleFurnitureAssignment(item);
          }
        } else {
          handleAddItem(item, type);
        }
    };

    const handleMaterialSelect = (entityId, newIndex) => {
        updateEntities(entities.map(ent => {
            if (ent.id === entityId) {
                return {
                    ...ent,
                    activeMaterialIndex: newIndex,
                };
            }
            return ent;
        }));
    };

    const handleFurnitureSelect = (entityId, newIndex) => {
        updateEntities(entities.map(ent => {
            if (ent.id === entityId) {
                return {
                    ...ent,
                    activeFurnitureIndex: newIndex,
                };
            }
            return ent;
        }));
    };

    const renderPreview = () => {
        const invScale = 1 / (transform.k || 1);
        if (tool === 'polygon' && drawingPoints.length > 0 && currentMouseData.pos) {
            const pts = [...drawingPoints, currentMouseData.pos];
            return <polyline points={pts.map(p => `${p.x},${p.y}`).join(' ')} fill="none" stroke="#3b82f6" strokeWidth={2 * invScale} strokeDasharray="5,5" />;
        }
        if (dragStart && currentMouseData.pos) {
            const { pos: currentMousePos, shiftKey, altKey } = currentMouseData;
            if (tool === 'rect') {
                const x = Math.min(dragStart.x, currentMousePos.x);
                const y = Math.min(dragStart.y, currentMousePos.y);
                const w = Math.abs(currentMousePos.x - dragStart.x);
                const h = Math.abs(currentMousePos.y - dragStart.y);
                return <rect x={x} y={y} width={w} height={h} fill="rgba(59, 130, 246, 0.2)" stroke="#3b82f6" strokeWidth={2 * invScale} strokeDasharray="5,5" />;
            }
            if (tool === 'ellipse') {
                let cx, cy, rx, ry;
                if (altKey) {
                    cx = dragStart.x;
                    cy = dragStart.y;
                    rx = Math.abs(currentMousePos.x - dragStart.x);
                    ry = Math.abs(currentMousePos.y - dragStart.y);
                } else {
                    cx = dragStart.x + (currentMousePos.x - dragStart.x) / 2;
                    cy = dragStart.y + (currentMousePos.y - dragStart.y) / 2;
                    rx = Math.abs(currentMousePos.x - dragStart.x) / 2;
                    ry = Math.abs(currentMousePos.y - dragStart.y) / 2;
                }
                
                if (shiftKey) {
                    rx = ry = Math.max(rx, ry);
                }

                return <ellipse cx={cx} cy={cy} rx={rx} ry={ry} fill="rgba(59, 130, 246, 0.2)" stroke="#3b82f6" strokeWidth={2 * invScale} strokeDasharray="5,5" />;
            }
        }
        return null;
    };

    const canvasStyle = {
        touchAction: 'none',
        backgroundImage: `radial-gradient(${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'} 1px, transparent 1px)`,
        backgroundSize: `${50 * transform.k}px ${50 * transform.k}px`,
        backgroundPosition: `${transform.x}px ${transform.y}px`,
        cursor: isPanning ? 'grabbing' : (['polygon', 'rect', 'ellipse'].includes(tool) ? 'crosshair' : 'default'),
        transition: isPanning ? 'none' : 'transform 0.6s ease-in-out',
    };
    
    useEffect(() => {
        const handleKeyDown = (e) => {
            const isTyping = ['input', 'textarea', 'select'].includes(e.target.tagName.toLowerCase());
            if (isTyping) return;

            const isMac = navigator.userAgent.includes('Mac');
            const isModifier = isMac ? e.metaKey : e.ctrlKey;
            
            if (isModifier && e.key.toLowerCase() === 'z') {
                e.preventDefault();
                if (e.shiftKey) {
                    redo();
                } else {
                    undo();
                }
            } else if (!isMac && e.ctrlKey && e.key.toLowerCase() === 'y') {
                e.preventDefault();
                redo();
            } else if (e.key === 'Delete' || e.key === 'Backspace') {
                if (singleSelectedEntityId && canEdit) {
                    handleDeleteSelectedEntity();
                }
            }
        };

        document.addEventListener('keydown', handleKeyDown);

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [undo, redo, singleSelectedEntityId, canEdit]);

    const handleOpenFeedback = (elementId, context) => {
        setFeedbackElement({ id: elementId, context: context });
    };

    const handleTogglePreview = (newIsPreview) => {
        setIsPreview(newIsPreview);
        if (!newIsPreview) {
            setFocusedAreaId(null);
            handleRecenter();
        } else {
            if (selectedEntity) {
                if (['polygon', 'rect', 'ellipse'].includes(selectedEntity.type)) {
                    setFocusedAreaId(selectedEntity.id);
                } else if (selectedEntity.type === 'image' && selectedEntity.linkedAreaId) {
                    setFocusedAreaId(selectedEntity.linkedAreaId);
                } else {
                    setFocusedAreaId(null);
                }
            } else {
                setFocusedAreaId(null);
            }
        }
    };

    const handleConfirmItem = (targetId, category, options = {}) => {
        if (currentUser?.role !== 'customer') return;

        const confirmationData = {
            confirmedBy: currentUser.uid,
            confirmedAt: new Date().toISOString()
        };

        let newEntities;

        if (category === 'furniture') {
            newEntities = entities.map(e => {
                if (e.id === targetId) {
                    const activeIndex = e.activeFurnitureIndex || 0;
                    const activeFurnitureId = e.furnitureIds?.[activeIndex];
        
                    if (!activeFurnitureId) return e; // No active furniture to confirm
        
                    // Check if the currently active furniture is already confirmed
                    if (e.confirmedFurniture?.furnitureId === activeFurnitureId) {
                        // If it is, un-confirm it
                        const { confirmedFurniture, ...rest } = e;
                        return rest;
                    } else {
                        // If not, confirm the new one
                        return { 
                            ...e, 
                            confirmedFurniture: { 
                                furnitureId: activeFurnitureId, 
                                ...confirmationData 
                            } 
                        };
                    }
                }
                return e;
            });
        } else { // This else handles all material types
            const { materialId, edgeIndex, floorSlotKey, wallSlotKey } = options;
            if (!materialId) return;

            newEntities = entities.map(e => {
                if (e.id === targetId) { 
                    const updatedArea = { ...e };

                    if (category === 'customWall' && edgeIndex !== undefined && updatedArea.edges?.[edgeIndex]) {
                        const newEdges = [...updatedArea.edges];
                        const currentEdge = { ...newEdges[edgeIndex] };
                        
                        if (currentEdge.confirmedWallMaterial?.materialId === materialId) {
                            delete currentEdge.confirmedWallMaterial;
                        } else {
                            currentEdge.confirmedWallMaterial = { materialId, ...confirmationData };
                        }
                        newEdges[edgeIndex] = currentEdge;
                        updatedArea.edges = newEdges;
                        
                    } else if (category === 'floor' && floorSlotKey && updatedArea.floorSlots) {
                        const newSlots = updatedArea.floorSlots.map(slot => {
                            if (slot.key === floorSlotKey) {
                                const newSlot = { ...slot };
                                if (newSlot.confirmedMaterial?.materialId === materialId) {
                                    delete newSlot.confirmedMaterial;
                                } else {
                                    newSlot.confirmedMaterial = { materialId, ...confirmationData };
                                }
                                return newSlot;
                            }
                            return slot;
                        });
                        updatedArea.floorSlots = newSlots;
                    } else if (category === 'wall' && wallSlotKey && updatedArea.wallSlots) {
                        const newSlots = updatedArea.wallSlots.map(slot => {
                            if (slot.key === wallSlotKey) {
                                const newSlot = { ...slot };
                                if (newSlot.confirmedMaterial?.materialId === materialId) {
                                    delete newSlot.confirmedMaterial;
                                } else {
                                    newSlot.confirmedMaterial = { materialId, ...confirmationData };
                                }
                                return newSlot;
                            }
                            return slot;
                        });
                        updatedArea.wallSlots = newSlots;
                    } else if (category !== 'customWall') {
                        const currentConfirmed = { ...(updatedArea.confirmedMaterials || {}) };
                        
                        if (currentConfirmed[category]?.materialId === materialId) {
                            delete currentConfirmed[category];
                        } else {
                            currentConfirmed[category] = { materialId, ...confirmationData };
                        }
                        updatedArea.confirmedMaterials = currentConfirmed;
                    }
                    return updatedArea;
                }
                return e;
            });
        }
        updateEntities(newEntities);
    };

    const isAreaComplete = useCallback((areaId) => {
        const area = entities.find(e => e.id === areaId);
        if (!area) return false;
    
        const findBox = (filters) => entities.find(e => 
            e.linkedAreaId === areaId &&
            e.purpose === 'material' &&
            Object.entries(filters).every(([key, value]) => e[key] === value)
        );
    
        const getActiveMaterialId = (box) => {
            if (!box || !box.materialIds || box.materialIds.length === 0) return null;
            const activeIndex = box.activeMaterialIndex || 0;
            return box.materialIds[activeIndex];
        };
    
        if (area.ceilingMaterials && area.ceilingMaterials.length > 0) {
            const box = findBox({ materialCategory: 'ceiling' });
            const activeMaterialId = getActiveMaterialId(box);
            if (area.confirmedMaterials?.ceiling?.materialId !== activeMaterialId) {
                return false;
            }
        }
    
        if (area.floorSlots) {
            for (const slot of area.floorSlots) {
                if (slot.materialIds && slot.materialIds.length > 0) {
                    const box = findBox({ materialCategory: 'floor', floorSlotKey: slot.key });
                    const activeMaterialId = getActiveMaterialId(box);
                    if (slot.confirmedMaterial?.materialId !== activeMaterialId) {
                        return false;
                    }
                }
            }
        }
        
        if (area.wallSlots) {
            for (const slot of area.wallSlots) {
                if (slot.materialIds && slot.materialIds.length > 0) {
                    const box = findBox({ materialCategory: 'wall', wallSlotKey: slot.key });
                    const activeMaterialId = getActiveMaterialId(box);
                    if (slot.confirmedMaterial?.materialId !== activeMaterialId) {
                        return false;
                    }
                }
            }
        }
    
        if (area.edges) {
            for (const [index, edge] of area.edges.entries()) {
                if (edge.wallMaterials && edge.wallMaterials.length > 0) {
                    const box = findBox({ materialCategory: 'customWall', edgeIndex: index });
                    const activeMaterialId = getActiveMaterialId(box);
                    if (edge.confirmedWallMaterial?.materialId !== activeMaterialId) {
                        return false;
                    }
                }
            }
        }
    
        const linkedFurniture = entities.filter(e => 
            e.linkedAreaId === areaId && 
            e.type === 'image' && 
            e.purpose === 'furniture'
        );
    
        if (linkedFurniture.length > 0) {
            if (!linkedFurniture.every(f => !!f.confirmedFurniture?.furnitureId)) {
                return false;
            }
        }
        
        return true;
    
    }, [entities]);

    const tools = [
        { id: 'select', icon: MousePointer2, label: 'Select' },
        { id: 'polygon', icon: Spline, label: 'Polygon Area' },
        { id: 'rect', icon: Square, label: 'Rectangle Area' },
        { id: 'ellipse', icon: Circle, label: 'Ellipse Area' },
    ];
    
    const historyButtons = (
        <>
            <div className="h-6 w-px bg-slate-200 dark:bg-gray-600 mx-1"></div>
            <button
                onClick={undo}
                disabled={!canUndo || isSaving}
                className="p-2 rounded-md transition-colors text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Undo (Ctrl+Z)"
            >
                <Undo className="w-5 h-5" />
            </button>
            <button
                onClick={redo}
                disabled={!canRedo || isSaving}
                className="p-2 rounded-md transition-colors text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Redo (Ctrl+Y)"
            >
                <Redo className="w-5 h-5" />
            </button>
            <div className="h-6 w-px bg-slate-200 dark:bg-gray-600 mx-1"></div>
            <button
                onClick={handleSave}
                disabled={isSaving || !hasUnsavedChanges}
                className="p-2 rounded-md transition-colors text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700 disabled:opacity-50"
                title="Save"
            >
                <Save className="w-5 h-5" />
            </button>
        </>
    );

    return (
        <div className="flex flex-col h-full bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 overflow-hidden">
             {canManageProject && <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileSelect}
                accept="application/pdf"
                className="hidden"
            />}
            <div className="bg-white dark:bg-gray-800/50 border-b dark:border-gray-700 p-2 hidden md:flex items-center justify-center gap-8 shadow-inner">
                <div className="flex items-center gap-3 w-56">
                    <label className="text-xs font-medium text-gray-600 dark:text-gray-400 flex-shrink-0">Plan</label>
                    <input 
                        type="range" 
                        min="0" 
                        max="1" 
                        step="0.05" 
                        value={planOpacity} 
                        onChange={(e) => setPlanOpacity(parseFloat(e.target.value))}
                        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-gray-600 accent-blue-600"
                    />
                    <span className="text-xs font-mono text-gray-500 dark:text-gray-400 w-10 text-right">{Math.round(planOpacity * 100)}%</span>
                </div>
                <div className="flex items-center gap-3 w-56">
                    <label className="text-xs font-medium text-gray-600 dark:text-gray-400 flex-shrink-0">Elements</label>
                    <input 
                        type="range" 
                        min="0" 
                        max="1" 
                        step="0.05" 
                        value={entitiesOpacity} 
                        onChange={(e) => setEntitiesOpacity(parseFloat(e.target.value))}
                        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-gray-600 accent-blue-600"
                    />
                    <span className="text-xs font-mono text-gray-500 dark:text-gray-400 w-10 text-right">{Math.round(entitiesOpacity * 100)}%</span>
                </div>
            </div>
            <div className="flex-grow min-h-0 relative">
                <div className="hidden md:flex flex-col h-full absolute top-0 left-0 w-full">
                    <div className="relative flex-grow">
                        <div
                            ref={desktopCanvasRef}
                            className="items-center justify-center bg-gray-100 dark:bg-gray-900 overflow-hidden relative h-full w-full"
                            style={canvasStyle}
                            onMouseDown={handleMouseDown}
                            onMouseMove={handleMouseMove}
                            onMouseUp={handleMouseUp}
                            onMouseLeave={handleMouseUp}
                            onContextMenu={handleRightClick}
                            onDragEnter={handleCanvasDragEnter}
                            onDragLeave={handleCanvasDragLeave}
                            onDragOver={handleCanvasDragOver}
                            onDrop={handleCanvasDrop}
                        >
                            {isDraggingOverCanvas && (
                                <div className="absolute inset-0 bg-blue-500/10 border-4 border-dashed border-blue-500 rounded-lg flex flex-col justify-center items-center pointer-events-none z-20">
                                    <Upload className="h-12 w-12 text-blue-500" />
                                    <p className="mt-2 text-lg font-semibold text-blue-600">Drop Image to Upload</p>
                                </div>
                            )}
                            
                            <CanvasRenderer
                                isPanning={isPanning}
                                transform={transform}
                                pdfPage={pdfPage}
                                planOpacity={planOpacity}
                                renderableEntities={renderableEntities}
                                entitiesOpacity={entitiesOpacity}
                                unreadCounts={unreadCounts}
                                handleOpenFeedback={handleOpenFeedback}
                                entities={entities}
                                selectedEntity={selectedEntity}
                                selectedEntityIds={selectedEntityIds}
                                singleSelectedEntityId={singleSelectedEntityId}
                                focusedArea={focusedArea}
                                isDark={isDark}
                                tool={tool}
                                canEdit={canEdit}
                                handleEntityClick={handleEntityClick}
                                onContextMenu={handleContextMenu}
                                onImageOrHandleMouseDown={onImageOrHandleMouseDown}
                                onAnchorMouseDown={onAnchorMouseDown}
                                selectedEdgeIndices={selectedEdgeIndices}
                                handleEdgeClick={handleEdgeClick}
                                renderPreview={renderPreview}
                                displayMode={displayMode}
                                materials={materials}
                                furniture={furniture}
                                decorativeImages={decorativeImages}
                                handleMaterialSelect={handleMaterialSelect}
                                handleFurnitureSelect={handleFurnitureSelect}
                                masterStyle={project.masterStyle}
                                showConfirmations={showConfirmations}
                                onConfirmItem={handleConfirmItem}
                                currentUser={currentUser}
                                effectiveIsPreview={effectiveIsPreview}
                                draggedImageState={draggedImageState}
                                draggedAnchorState={draggedAnchorState}
                            />

                            {isRendering && (
                                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-20 flex items-center space-x-2 p-3 text-blue-600 bg-white/90 dark:bg-gray-800/90 rounded-lg shadow-lg border border-blue-100 dark:border-blue-800 backdrop-blur-sm">
                                    <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                                    <span className="text-sm font-bold">Rendering PDF...</span>
                                </div>
                            )}
                            
                        </div>

                        <LeftPanel
                            isOpen={isLeftPanelOpen}
                            setIsOpen={setIsLeftPanelOpen}
                            canManageProject={canManageProject}
                            handleAddFloor={handleAddFloor}
                            floorPlans={floorPlans}
                            loading={loading}
                            handlePlanDragStart={handlePlanDragStart}
                            handlePlanDragEnd={handlePlanDragEnd}
                            handlePlanDragOver={handlePlanDragOver}
                            handleDragLeaveContainer={handleDragLeaveContainer}
                            handlePlanDrop={handlePlanDrop}
                            handleCardDrop={handleCardDrop}
                            draggedPlanId={draggedPlanId}
                            dropIndicator={dropIndicator}
                            currentPlanStates={currentPlanStates}
                            activePlanId={activePlanId}
                            editingPlanId={editingPlanId}
                            handleDoubleClick={handleDoubleClick}
                            handleSetActive={handleSetActive}
                            tempName={tempName}
                            setTempName={setTempName}
                            handleNameChange={handleNameChange}
                            handleKeyDown={handleKeyDown}
                            triggerFileUpload={triggerFileUpload}
                            handleDeleteRequest={handleDeleteRequest}
                            selectedEntityId={singleSelectedEntityId}
                            onSelectEntity={handleSelectEntityFromPanel}
                            uploadProgress={uploadProgress}
                            expandedPlanId={expandedPlanId}
                            handleToggleExpand={handleToggleExpand}
                            mobileView={mobileView}
                            setMobileView={setMobileView}
                            draggedOverPlanId={draggedOverPlanId}
                            handleDragEnterCard={handleDragEnterCard}
                            handleDragLeaveCard={handleDragLeaveCard}
                            isAreaComplete={isAreaComplete}
                        />

                        <RightPanel
                            isOpen={isRightPanelOpen}
                            setIsOpen={setIsRightPanelOpen}
                            canManageProperties={canManageProperties}
                            selectedEntity={selectedEntity}
                            selectedEntityIds={selectedEntityIds}
                            onPropertyChange={handlePropertyChange}
                            onDelete={handleDeleteSelectedEntity}
                            canEdit={canEdit}
                            isDark={isDark}
                            handleRestoreAspectRatio={handleRestoreAspectRatio}
                            materials={materials}
                            furniture={furniture}
                            decorativeImages={decorativeImages}
                            openLibrary={(targetKey, options) => openLibrary('assign', { key: targetKey, ...options })}
                            onSelectEdge={handleSelectEdgeFromPanel}
                            selectedEdgeIndices={selectedEdgeIndices}
                            onClearEdgeSelection={handleClearEdgeSelection}
                            onDeleteCustomEdge={handleDeleteCustomEdge}
                            entities={entities}
                            onAddFurnitureClick={() => openLibrary('add', {initialTab: 'furniture'})}
                            onAddFurnitureOption={() => openLibrary('assign', {key: 'furnitureIds', index: -1, type: 'entity', initialTab: 'furniture'})}
                            onSelectEntity={setSelectedEntityIds}
                            onEditItem={handleOpenEditor}
                        />
                    </div>
                </div>

                <TopToolbar
                    tools={tools}
                    tool={tool}
                    setTool={setTool}
                    canEdit={canEdit}
                    setIsLibraryOpen={() => openLibrary('add', {initialTab: 'decorative'})}
                    onAddImageClick={() => openLibrary('add', {initialTab: 'decorative'})}
                    isPreview={effectiveIsPreview}
                    onTogglePreview={handleTogglePreview}
                    canManageProject={canManageProject}
                    historyButtons={historyButtons}
                    saveStatus={saveStatus}
                    currentUser={currentUser}
                    showConfirmations={showConfirmations}
                    onToggleConfirmations={setShowConfirmations}
                />
                
                <FloatingMoodboardBar
                    isVisible={ (!!selectedEntity && ['polygon', 'rect', 'ellipse'].includes(selectedEntity.type)) || !!focusedArea }
                    displayMode={displayMode}
                    onDisplayModeChange={setDisplayMode}
                />

                <MobileView
                    isPanning={isPanning}
                    mobileView={mobileView}
                    setMobileView={setMobileView}
                    canManageProject={canManageProject}
                    handleAddFloor={handleAddFloor}
                    floorPlans={floorPlans}
                    loading={loading}
                    handlePlanDragStart={handlePlanDragStart}
                    handlePlanDragEnd={handlePlanDragEnd}
                    handlePlanDragOver={handlePlanDragOver}
                    handleDragLeaveContainer={handleDragLeaveContainer}
                    handlePlanDrop={handlePlanDrop}
                    handleCardDrop={handleCardDrop}
                    draggedPlanId={draggedPlanId}
                    dropIndicator={dropIndicator}
                    currentPlanStates={currentPlanStates}
                    activePlanId={activePlanId}
                    editingPlanId={editingPlanId}
                    handleDoubleClick={handleDoubleClick}
                    handleSetActive={handleSetActive}
                    tempName={tempName}
                    setTempName={setTempName}
                    handleNameChange={handleNameChange}
                    handleKeyDown={handleKeyDown}
                    triggerFileUpload={triggerFileUpload}
                    handleDeleteRequest={handleDeleteRequest}
                    selectedEntityId={singleSelectedEntityId}
                    setSelectedEntityId={(id) => setSelectedEntityIds([id])}
                    uploadProgress={uploadProgress}
                    expandedPlanId={expandedPlanId}
                    handleToggleExpand={handleToggleExpand}
                    mobileCanvasRef={mobileCanvasRef}
                    canvasStyle={canvasStyle}
                    handlePointerDown={handlePointerDown}
                    handlePointerMove={handlePointerMove}
                    handlePointerUp={handlePointerUp}
                    tools={tools}
                    tool={tool}
                    setTool={setTool}
                    canEdit={canEdit}
                    historyButtons={historyButtons}
                    saveStatus={saveStatus}
                    isMobileOpacityPanelOpen={isMobileOpacityPanelOpen}
                    setIsMobileOpacityPanelOpen={setIsMobileOpacityPanelOpen}
                    planOpacity={planOpacity}
                    setPlanOpacity={setPlanOpacity}
                    entitiesOpacity={entitiesOpacity}
                    setEntitiesOpacity={setEntitiesOpacity}
                    transform={transform}
                    pdfPage={pdfPage}
                    renderableEntities={renderableEntities}
                    unreadCounts={unreadCounts}
                    handleOpenFeedback={handleOpenFeedback}
                    entities={entities}
                    selectedEntity={selectedEntity}
                    selectedEntityIds={selectedEntityIds}
                    focusedArea={focusedArea}
                    isDark={isDark}
                    handleEntityClick={handleEntityClick}
                    onImageOrHandleMouseDown={onImageOrHandleMouseDown}
                    onAnchorMouseDown={onAnchorMouseDown}
                    selectedEdgeIndices={selectedEdgeIndices}
                    handleEdgeClick={handleEdgeClick}
                    renderPreview={renderPreview}
                    isRendering={isRendering}
                    canManageProperties={canManageProperties}
                    onPropertyChange={handlePropertyChange}
                    handleDeleteSelectedEntity={handleDeleteSelectedEntity}
                    handleRestoreAspectRatio={handleRestoreAspectRatio}
                    materials={materials}
                    furniture={furniture}
                    decorativeImages={decorativeImages}
                    handleMaterialSelect={handleMaterialSelect}
                    handleFurnitureSelect={handleFurnitureSelect}
                    draggedOverPlanId={draggedOverPlanId}
                    handleDragEnterCard={handleDragEnterCard}
                    handleDragLeaveCard={handleDragLeaveCard}
                    isAreaComplete={isAreaComplete}
                    onConfirmItem={handleConfirmItem}
                    currentUser={currentUser}
                    effectiveIsPreview={effectiveIsPreview}
                    showConfirmations={showConfirmations}
                    onToggleConfirmations={setShowConfirmations}
                    masterStyle={project.masterStyle}
                    draggedImageState={draggedImageState}
                    draggedAnchorState={draggedAnchorState}
                    displayMode={displayMode}
                    singleSelectedEntityId={singleSelectedEntityId}
                />
            </div>
            {feedbackElement && (
                <MessagingPanel 
                    projectId={projectId}
                    element={entities.find(e => e.id === feedbackElement.id)}
                    context={feedbackElement.context}
                    currentUser={currentUser}
                    participants={projectParticipants}
                    allUsers={users}
                    onClose={() => setFeedbackElement(null)}
                />
            )}
            <DeleteConfirmationModal 
                isOpen={!!nodeToDelete}
                onClose={() => setNodeToDelete(null)}
                onConfirm={handleConfirmDelete}
                item={nodeToDelete}
                isDeleting={isDeleting}
            />
            {canEdit && (
                <CombinedLibrary
                    isOpen={isLibraryOpen}
                    onClose={() => setIsLibraryOpen(false)}
                    ownerId={materialOwnerId}
                    onSelectItem={handleItemSelectionFromLibrary}
                    canManage={canEdit}
                    initialTab={libraryInitialTab}
                    onEditItem={handleOpenEditor}
                    purpose={libraryPurpose}
                />
            )}
            <ImageInfoModal
                isOpen={!!imageInfoModalData}
                onClose={() => setImageInfoModalData(null)}
                imageData={imageInfoModalData}
            />
             <ContextMenu
                x={contextMenu.x}
                y={contextMenu.y}
                show={contextMenu.show}
                onClose={() => setContextMenu({ show: false, x: 0, y: 0, entityId: null })}
                onBringToFront={handleBringToFront}
                onSendToBack={handleSendToBack}
            />
            {editingItem.type === 'material' && (
                <MaterialEditor
                    isOpen={true}
                    onClose={handleCloseEditor}
                    onSave={handleSaveItem}
                    materialToEdit={editingItem.item}
                    ownerId={materialOwnerId}
                />
            )}
             {editingItem.type === 'furniture' && (
                <FurnitureEditor
                    isOpen={true}
                    onClose={handleCloseEditor}
                    onSave={handleSaveItem}
                    furnitureToEdit={editingItem.item}
                    ownerId={materialOwnerId}
                />
            )}
             {editingItem.type === 'decorative' && (
                <DecorativeImageEditor
                    isOpen={true}
                    onClose={handleCloseEditor}
                    onSave={handleSaveItem}
                    imageToEdit={editingItem.item}
                    ownerId={materialOwnerId}
                />
            )}
        </div>
    );
};

export default Moodboard;
